import { Emitter } from '@wonderlandengine/api';
import { EventsClient } from '../events-client.js';
import { MenuController } from '../menus/menu-controller.js';
import { UserMessaging } from '../utils/user-messaging.js';
import Logger from '../utils/logger.js';
import { UrlVideoSync } from './url-video-sync.js';

const MODULE = 'SharedMediaController';

export interface SharedMediaCaptureConstraints {
    videoWidthIdeal: number;
    videoWidthMax: number;
    videoHeightIdeal: number;
    videoHeightMax: number;
    videoFrameRateIdeal: number;
    videoFrameRateMax: number;
}

export type SharedMediaPreviewSource =
    | { kind: 'stream'; stream: MediaStream }
    | { kind: 'url'; url: string; startAtMs: number; seekSeconds: number };

/**
 * True singleton that owns everything about the locally chosen / room-wide shared media:
 * capture, the P2P broadcast audio graph, broadcast negotiation and URL playback state.
 *
 * Rendering is intentionally not handled here. `SharedMediaVideoTexture` components
 * subscribe to {@link onPreviewSourceChanged} and draw {@link previewVideo}, which allows
 * any number of preview surfaces (web menu, VR menu, …) to exist in a scene.
 */
export class SharedMediaController {
    private static _instance: SharedMediaController | null = null;

    static get instance(): SharedMediaController {
        if (!SharedMediaController._instance) {
            SharedMediaController._instance = new SharedMediaController();
        }
        return SharedMediaController._instance;
    }

    /** Notifies preview renderers that {@link previewVideo} now carries a different source. */
    readonly onPreviewSourceChanged: Emitter<[SharedMediaPreviewSource | null]> =
        new Emitter<[SharedMediaPreviewSource | null]>();

    previewVideo: HTMLVideoElement | null = null;

    captureConstraints: SharedMediaCaptureConstraints = {
        videoWidthIdeal: 1280,
        videoWidthMax: 1920,
        videoHeightIdeal: 720,
        videoHeightMax: 1080,
        videoFrameRateIdeal: 24,
        videoFrameRateMax: 30,
    };

    private previewSource: SharedMediaPreviewSource | null = null;
    private readonly urlSync = new UrlVideoSync();

    private localShareAudioGain = 1;
    private p2pAudioContext: AudioContext | null = null;
    private p2pAudioSourceNode: MediaStreamAudioSourceNode | null = null;
    private p2pAudioGainNode: GainNode | null = null;
    private p2pAudioCompressorNode: DynamicsCompressorNode | null = null;
    private p2pAudioDestination: MediaStreamAudioDestinationNode | null = null;
    private p2pBroadcastStream: MediaStream | null = null;
    private localCaptureStream: MediaStream | null = null;

    private attachedEventsClient: EventsClient | null = null;
    private attachedMenuController: MenuController | null = null;

    private readonly onUserChoiceHandler = this.onUserChoice.bind(this);
    private readonly onSyncUrlVideoStartHandler = this.onSyncUrlVideoStart.bind(this);
    private readonly onSyncUrlVideoPingHandler = this.onSyncUrlVideoPing.bind(this);
    private readonly onUserJoinHandler = this.onUserJoin.bind(this);

    private constructor() {}

    get currentPreviewSource(): SharedMediaPreviewSource | null {
        return this.previewSource;
    }

    /**
     * Idempotent wiring to the network/menu singletons. Called from both `EventsClient.start()`
     * and preview component startup because either may become available first.
     */
    attach(): void {
        const eventsClient = EventsClient.instanceOrNull;
        if (eventsClient && this.attachedEventsClient !== eventsClient) {
            this.attachedEventsClient = eventsClient;
            this.localShareAudioGain = eventsClient.getBroadcastShareAudioMixGain();
            eventsClient.onSyncUrlVideoStartEvent.add(this.onSyncUrlVideoStartHandler);
            eventsClient.onSyncUrlVideoPingEvent.add(this.onSyncUrlVideoPingHandler);
        }

        const menuController = MenuController.instance;
        if (menuController && this.attachedMenuController !== menuController) {
            this.attachedMenuController = menuController;
            menuController.onUserChoice.add(this.onUserChoiceHandler);
        }
    }

    setCaptureConstraints(constraints: SharedMediaCaptureConstraints): void {
        this.captureConstraints = constraints;
    }

    setLocalShareAudioGain(gain: number): void {
        const normalizedGain = Number.isFinite(gain) ? Math.max(0, Math.min(2, gain)) : 1;
        this.localShareAudioGain = normalizedGain;
        if (this.p2pAudioGainNode && this.p2pAudioContext) {
            this.p2pAudioGainNode.gain.setTargetAtTime(normalizedGain, this.p2pAudioContext.currentTime, 0.015);
        }
    }

    private onUserChoice(eventType: string, eventData: object | undefined): void {
        Logger.debug(MODULE, 'User choice event: ' + eventType);
        switch (eventType) {
            case 'choose-screenshare-media':
                void this.loadShare(false);
                break;
            case 'choose-camerashare-media':
                void this.loadShare(true);
                break;
            case 'broadcast-chosen-media':
                void this.broadcastVideoStream(!EventsClient.instance.serverBasedBroadcast);
                break;
            case 'stop-broadcasting-media':
                this.stopBroadcasting();
                break;
            case 'broadcast-url-media':
                this.requestUrlBroadcast(eventData);
                break;
        }
    }

    private requestUrlBroadcast(eventData: object | undefined): void {
        const payload = (eventData ?? {}) as {
            url?: string;
            seekSeconds?: number;
            enableResyncPings?: boolean;
        };
        const url = typeof payload.url === 'string' ? payload.url.trim() : '';
        const seekSeconds = Number.isFinite(payload.seekSeconds) ? Number(payload.seekSeconds) : 0;
        const enableResyncPings = payload.enableResyncPings !== false;

        if (!url) {
            Logger.warn(MODULE, 'Ignoring broadcast-url-media request with empty URL');
            return;
        }

        EventsClient.instance.requestSynchronizedUrlVideoStart(url, seekSeconds, 3000, {
            enableResyncPings,
        });
    }

    // ---------------------------------------------------------------- preview source

    private ensurePreviewVideo(): HTMLVideoElement {
        if (!this.previewVideo) {
            this.previewVideo = document.createElement('video');
            this.previewVideo.crossOrigin = 'anonymous';
            this.previewVideo.playsInline = true;
        }

        // Previews never carry audio: shared audio is mixed by EventsClient / the room screens.
        this.previewVideo.muted = true;
        return this.previewVideo;
    }

    private setPreviewSource(source: SharedMediaPreviewSource | null): void {
        this.previewSource = source;
        this.onPreviewSourceChanged.notify(source);
    }

    private onSyncUrlVideoStart(data: { url: string; startAtMs: number; requestedBy: number; seekSeconds: number }): void {
        // The preview shows what *this* user is sharing. Someone else's URL share belongs on
        // the room screens only, and mirroring it here would cost a second fetch of the media.
        if (data.requestedBy !== EventsClient.instance?.playerUserId) {
            return;
        }

        const video = this.ensurePreviewVideo();
        this.destroyP2PAudioGraph();
        this.clearLocalCaptureStream();
        this.setWebMenuPreviewStream(null);

        video.pause();
        if (!this.urlSync.begin(video, data.url, data.startAtMs, data.seekSeconds)) {
            return;
        }

        this.setPreviewSource({
            kind: 'url',
            url: data.url,
            startAtMs: data.startAtMs,
            seekSeconds: data.seekSeconds,
        });
    }

    private onSyncUrlVideoPing(data: { url: string; startAtMs: number; requestedBy: number; seekSeconds: number }): void {
        if (!this.previewVideo || this.previewSource?.kind !== 'url') {
            return;
        }

        this.urlSync.applyPing(this.previewVideo, data.url, data.startAtMs, data.seekSeconds);
    }

    async loadShare(loadCamera: boolean): Promise<void> {
        this.urlSync.clear();
        this.destroyP2PAudioGraph();
        this.clearLocalCaptureStream();
        this.setWebMenuPreviewStream(null);
        this.setPreviewSource(null);

        const constraints = this.captureConstraints;
        const videoConstraints: MediaTrackConstraints = {
            width: { ideal: constraints.videoWidthIdeal, max: constraints.videoWidthMax },
            height: { ideal: constraints.videoHeightIdeal, max: constraints.videoHeightMax },
            frameRate: { ideal: constraints.videoFrameRateIdeal, max: constraints.videoFrameRateMax },
        };

        let stream: MediaStream;
        try {
            stream = loadCamera
                ? await navigator.mediaDevices.getUserMedia({ audio: true, video: videoConstraints })
                : await navigator.mediaDevices.getDisplayMedia({
                      audio: {
                          autoGainControl: false,
                          channelCount: 2,
                          echoCancellation: false,
                          latency: 0,
                          noiseSuppression: false,
                          sampleRate: 48000,
                          sampleSize: 16,
                      } as MediaTrackConstraints,
                      video: videoConstraints,
                  });
        } catch (err) {
            const message = loadCamera
                ? 'Camera capture failed. Check browser permissions and secure context (HTTPS).'
                : 'Screen capture failed. Check browser permissions and secure context (HTTPS).';
            UserMessaging.instance.notify(message, 6.0);
            Logger.warn(MODULE, 'Failed to acquire capture stream', err);
            return;
        }

        this.localCaptureStream = stream;
        this.configureP2PBroadcastStream(stream);
        this.setWebMenuPreviewStream(stream);

        const video = this.ensurePreviewVideo();
        video.srcObject = stream;
        void video.play().catch((err) => {
            Logger.warn(MODULE, 'Preview playback failed to start', err);
        });

        this.setPreviewSource({ kind: 'stream', stream });
    }

    // ------------------------------------------------------------------- broadcasting

    async broadcastVideoStream(peerToPeer: boolean, receiverId: number | null = null): Promise<void> {
        const vsec = EventsClient.instance;
        const p2pStream = this.getP2PBroadcastStream();

        if (!this.localCaptureStream && p2pStream.getTracks().length === 0) {
            UserMessaging.instance.notify('No media selected. Choose Screen or Camera first.', 5.0);
            Logger.warn(MODULE, 'Broadcast requested without an active media stream');
            return;
        }

        await vsec?.loadTurnIceServers?.();

        if (peerToPeer) {
            const localTrackKinds = p2pStream
                .getTracks()
                .map((track) => `${track.kind}:${track.readyState}`)
                .join(', ');
            Logger.debug(MODULE, `P2P broadcast track set: [${localTrackKinds || 'none'}]`);

            if (!receiverId) {
                vsec?.createVidsharePeerConnections();

                for (const peerCon of vsec?.vidsharePeerConnections.values() ?? []) {
                    p2pStream.getTracks().forEach((track) => peerCon.addTrack(track, p2pStream));
                }

                // Ensure SDP reflects all senders (including audio) after track attachment.
                await vsec?.startVidshareNegotiations();
            } else {
                const vsPC = vsec?.createVidsharePeerConnection(receiverId);

                p2pStream.getTracks().forEach((track) => vsPC?.addTrack(track, p2pStream));

                if (vsPC) {
                    await vsec?.startVidshareNegotiation(receiverId, vsPC);
                }
            }

            vsec?.onUserJoinEvent.add(this.onUserJoinHandler);
            vsec?.claimMasterShareRole();
        } else {
            await vsec.startMediasoupBroadcast(this.localCaptureStream ?? p2pStream);
            if (!vsec.sharingVideo) {
                Logger.warn(MODULE, 'Server-based broadcast did not start; retrying with peer-to-peer mode');
                UserMessaging.instance.notify('Server broadcast unavailable. Falling back to peer-to-peer broadcast.', 6.0);
                await this.broadcastVideoStream(true, receiverId);
            }
        }
    }

    stopBroadcasting(): void {
        EventsClient.instance.stopVidshare();
    }

    private onUserJoin(receiverId: number): void {
        const vsec = EventsClient.instance;
        if (!vsec?.sharingVideo) {
            return;
        }

        const vidStream = this.getP2PBroadcastStream();
        const vsPC = vsec.createVidsharePeerConnection(receiverId);

        vidStream.getTracks().forEach((track) => vsPC?.addTrack(track, vidStream));

        if (vsPC) {
            void vsec.startVidshareNegotiation(receiverId, vsPC);
        }
    }

    // ------------------------------------------------------------------- audio graph

    private configureP2PBroadcastStream(stream: MediaStream): void {
        this.destroyP2PAudioGraph();

        const audioTracks = stream.getAudioTracks().filter((track) => track.readyState === 'live');
        if (audioTracks.length === 0) {
            this.p2pBroadcastStream = stream;
            return;
        }

        this.p2pAudioContext = new AudioContext({ latencyHint: 'interactive', sampleRate: 48000 });
        this.p2pAudioSourceNode = this.p2pAudioContext.createMediaStreamSource(stream);
        this.p2pAudioGainNode = this.p2pAudioContext.createGain();
        this.p2pAudioGainNode.gain.value = this.localShareAudioGain;
        this.p2pAudioCompressorNode = this.p2pAudioContext.createDynamicsCompressor();
        this.p2pAudioCompressorNode.threshold.value = -9;
        this.p2pAudioCompressorNode.knee.value = 12;
        this.p2pAudioCompressorNode.ratio.value = 3;
        this.p2pAudioCompressorNode.attack.value = 0.003;
        this.p2pAudioCompressorNode.release.value = 0.12;
        this.p2pAudioDestination = this.p2pAudioContext.createMediaStreamDestination();

        this.p2pAudioSourceNode.connect(this.p2pAudioGainNode);
        this.p2pAudioGainNode.connect(this.p2pAudioCompressorNode);
        this.p2pAudioCompressorNode.connect(this.p2pAudioDestination);

        this.p2pBroadcastStream = new MediaStream();
        for (const videoTrack of stream.getVideoTracks()) {
            this.p2pBroadcastStream.addTrack(videoTrack);
        }
        for (const audioTrack of this.p2pAudioDestination.stream.getAudioTracks()) {
            this.p2pBroadcastStream.addTrack(audioTrack);
        }
    }

    getP2PBroadcastStream(): MediaStream {
        return this.p2pBroadcastStream || this.localCaptureStream || new MediaStream();
    }

    private destroyP2PAudioGraph(): void {
        this.p2pAudioSourceNode?.disconnect();
        this.p2pAudioGainNode?.disconnect();
        this.p2pAudioCompressorNode?.disconnect();

        for (const track of this.p2pBroadcastStream?.getAudioTracks() || []) {
            try {
                track.stop();
            } catch {
                // ignore
            }
        }

        if (this.p2pAudioContext) {
            void this.p2pAudioContext.close().catch(() => undefined);
        }

        this.p2pAudioSourceNode = null;
        this.p2pAudioGainNode = null;
        this.p2pAudioCompressorNode = null;
        this.p2pAudioDestination = null;
        this.p2pAudioContext = null;
        this.p2pBroadcastStream = null;
    }

    /** Drops the reference only; tracks stay live so an in-flight broadcast is not killed. */
    private clearLocalCaptureStream(): void {
        this.localCaptureStream = null;
    }

    private setWebMenuPreviewStream(stream: MediaStream | null): void {
        const preview = MenuController.instance?.sharePreviewVideo;
        if (!preview) {
            return;
        }

        if (preview.srcObject !== stream) {
            preview.srcObject = stream;
        }

        if (!stream) {
            return;
        }

        preview.muted = true;
        preview.autoplay = true;
        preview.playsInline = true;
        void preview.play().catch((err) => {
            Logger.warn(MODULE, 'Web menu preview play() was blocked or failed', err);
        });
    }
}
